<script setup lang="ts">
import type { EnvironmentColor } from '../utils/environment'

const runtimeConfig = useRuntimeConfig()
const { current: environment } = useEnvironment()

const version = computed(() => {
  const value = runtimeConfig.public.APP_VERSION
  return value ? `v${value}` : ''
})

const BAR_BG: Record<EnvironmentColor, string> = {
  error: 'bg-error-600',
  info: 'bg-info-500',
  warning: 'bg-warning-500',
  success: 'bg-success-500',
  neutral: 'bg-neutral-500',
}
const barClass = computed(() => [
  BAR_BG[environment.color],
  environment.isProd ? 'h-1' : 'h-0.5',
])

const { topMenuItems } = useGlobalShortcuts()
</script>

<template>
  <div
    :class="['pointer-events-none fixed inset-x-0 top-0 z-50', barClass]"
    aria-hidden="true"
  />
  <UDashboardGroup class="bg-muted" storage="local" unit="rem">
    <UDashboardSidebar
      class="border-default bg-default"
      :default-size="18"
      :min-size="18"
      :ui="{ footer: 'border-t border-default' }"
      collapsible
      resizable
    >
      <template #toggle>
        <UDashboardSidebarToggle variant="subtle" />
      </template>
      <template #header="{ collapsed }">
        <div class="flex flex-1 items-center gap-2">
          <UDropdownMenu :items="topMenuItems">
            <UButton variant="ghost">
              <RialticLogo class="h-5 dark:text-white" :icon="collapsed" />
              <UBadge v-if="!collapsed" color="primary">Admin</UBadge>
            </UButton>
          </UDropdownMenu>
          <EnvironmentSwitcher
            class="absolute top-4 transition"
            :class="{
              'right-2': !collapsed,
              'translate-x-[37px] translate-y-1 transform': collapsed,
            }"
            :compact="collapsed"
          />
          <div class="flex-1" />
        </div>
      </template>

      <template #default="{ collapsed }">
        <NavMenu :collapsed="collapsed" />
      </template>

      <template #footer="{ collapsed }">
        <div class="flex w-full flex-col gap-2 px-2 py-2">
          <div
            class="flex items-center gap-2"
            :class="{
              'justify-center': collapsed,
              'justify-between': !collapsed,
            }"
          >
            <span v-if="!collapsed && version" class="text-muted text-xs">
              {{ version }}
            </span>
            <AuthState v-slot="{ loggedIn, user }">
              <NuxtLink v-if="!loggedIn" to="/auth/auth0" external>
                Login
              </NuxtLink>

              <UButton
                to="/profile"
                :label="collapsed ? undefined : user?.name"
                color="neutral"
                size="md"
                trailing
                variant="ghost"
              >
                <template #trailing>
                  <UAvatar :src="user?.picture" size="sm" />
                </template>
              </UButton>
            </AuthState>
          </div>
        </div>
      </template>
    </UDashboardSidebar>

    <div class="w-full overflow-auto" data-scroll-root>
      <UDashboardNavbar class="visible lg:hidden" :ui="{ title: 'hidden' }" />
      <slot />
    </div>
  </UDashboardGroup>
</template>
